import React, { useEffect, useState } from 'react'
import { Box, Button } from '@mantine/core'
import { useSelector } from 'react-redux'
import { editProduct } from '../../../../../services/products'
import { notificationError, notificationSuccess } from '../../../../ui/Notifications'
import { useSummaryProductForm } from './schema/form'
import { SummaryFormProvider } from './hook/summary-form'
import AdditionalSummaryProduct from './section/AdditionalSummaryProduct'
import SummaryDataProduct from './section/SummaryDataProduct'
import SummaryCategoryProduct from './section/SummaryCategoryProduct'
import SummaryDetailProduct from './section/SummaryDetailProduct'
import SummaryLabelProduct from './section/SummaryLabelProduct'
import SummaryAdditionalData from './section/SummaryAdditionalData'
import SummarySupplierNoteProduct from './section/SummarySupplierNoteProduct'

const ProductSummary = ({ productId, reloadProduct }) => {
  const { isLoadingProduct, summary, minimumPayload, prices } = useSelector(state => state.product_detail)

  const form = useSummaryProductForm()
  const [loadingSubmit, setLoadingSubmit] = useState(false)

  const assignFormSummary = (value) => {
    const data = {
      title: value.title,
      slug: value.slug || '',
      description: value.description || '',

      visibility: value.visibility ? '1' : '0',
      not_show_in_catalog: value.notShowInCatalog ? '1' : '0',

      status: value.status?.name || '',
      export_only: value.exportOnly,

      priority: value.priority,

      supplier_id: value?.supplier?.id ? `${value.supplier.id}` : null,
      brand_id: value?.brand?.id ? `${value.brand.id}` : null,
      warehouses: value?.warehouse?.id ? `${value.warehouse.id}` : null,
      country_id: value.countryId ? `${value.countryId}` : null,
      province_id: value.provinceId ? `${value.provinceId}` : null,
      city_id: value.cityId ? `${value.cityId}` : null,
      district_id: value.districtId ? `${value.districtId}` : null,

      original_currency: value.originalCurrency,
      regular_price: value.regularPrice,
      sale_price: value.salePrice,
      reseller_price: value.resellerPrice,
      markup_percentage: value.markupPercentage,

      reward_point: value.rewardPoint,
      base_qty_review: value.baseQtyReview,
      base_average_review_point: value.baseAverageReviewPoint,

      quantity_unit: value.quantityUnit,
      quantity: value.quantity,
      minimum_qty: value.minimumQuantity,
      base_qty_sold: value.baseQtySold,
      group_qty: value.groupQty ?? 0,
      qty_per_cbm: value.quantityPerCbm ?? 0,
      sku: value.sku,
      ali_sku: value.aliSku,

      weight_unit: value.weightUnit,
      weight: value.weight,
      note: value.note || '',
      tags: value.tags,
      po_ship_from: value.poShipFrom ? `${value.poShipFrom}` : null,
      po_shipment_method: value.poShipmentMethod ? `${value.poShipmentMethod}` : null,
      dimension_unit: value.dimensionUnit,
      dimensions: JSON.parse(JSON.stringify(value.dimensions)) || [],
      categories: JSON.parse(JSON.stringify(value.categories)) || [],
    }

    form.initialize(data)
    form.reset()
    form.setValues(data)
  }

  useEffect(() => {
    if (!isLoadingProduct) {
      assignFormSummary(summary)
    }
    // eslint-disable-next-line
  }, [isLoadingProduct, summary])


  const handleSubmit = async (formData) => {
    setLoadingSubmit(true)
    const payload = {
      ...minimumPayload,
      ...formData,
    }
    const { bundlePrices } = prices
    if (bundlePrices.length > 0) {
      const updateWeightUnitBundlePrice = bundlePrices.map((item) => {
        return {
          ...item,
          weight_unit: formData.weight_unit,
        }
      })
      payload.relationships = updateWeightUnitBundlePrice
    }
    try {
      const response = await editProduct(productId, payload)
      if (response.success) {
        notificationSuccess('Success', 'Update Product Success')
        setLoadingSubmit(false)
        reloadProduct()
      }
    } catch (error) {
      setLoadingSubmit(false)
      const errorMessage = error.response.data.message
      const errorMessageForm = error.response.data.errors
      const errorSlug = errorMessageForm?.slug

      const message = errorSlug
        ? 'The product slug is already in use. Please use a different slug.'
        : errorMessage
          ? errorMessage
          : 'Please contact your administrator'
      notificationError('Update Product Error', message)

      if (errorMessageForm) {
        Object.keys(errorMessageForm).forEach((key) => {
          form.setFieldError(key, errorMessageForm[key][0])
        })
      }
    }
  }

  const handlingScrolledError = (error) => {
    const errorField = Object.keys(error)[0]
    if (errorField) {
      const inputNode = form.getInputNode(errorField)
      if (inputNode) {
        inputNode.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    }
  }

  const renderButtonUpdate = () => {
    const buttonStyle = {
      position: 'fixed',
      bottom: '25px',
      right: '55px',
      padding: '10px',
      zIndex: 1000,
    }

    return (
      <Button
        size='sm'
        style={buttonStyle}
        loading={loadingSubmit}
        loaderProps={{ type: 'dots' }}
        onClick={form.onSubmit((value) => { handleSubmit(value) }, (error) => { handlingScrolledError(error) })}
      >
        Update
      </Button>
    )
  }

  return (
    <SummaryFormProvider form={form}>
      <Box mb={20} mt={10}>
        <AdditionalSummaryProduct />

        <SummaryDataProduct />

        <SummaryCategoryProduct />

        <SummaryDetailProduct reloadProduct={reloadProduct} />

        <SummaryLabelProduct />

        <SummaryAdditionalData />

        <SummarySupplierNoteProduct />

        {renderButtonUpdate()}
      </Box>
    </SummaryFormProvider>
  )
}

export default ProductSummary